export interface GrapevineBrandFields {
  slug: string;
  vertical: string;
  contactName: string;
  contactEmail: string;
  homepageUrl: string;
  tier: string;
}

interface FormDataLike {
  append(name: string, value: string): void;
}

const text = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(",");
  }
  return value == null ? "" : String(value);
};

const firstPresent = (...values: unknown[]): string => {
  const value = values.find(
    (candidate) =>
      candidate !== undefined &&
      candidate !== null &&
      text(candidate).trim() !== "",
  );
  return text(value);
};

export const shouldShowGrapevineFields = (
  isPerformanceCreator: boolean,
): boolean => isPerformanceCreator;

export function getGrapevineValidationError(
  isPerformanceCreator: boolean,
  hubspotCompanyId: string,
  fields: GrapevineBrandFields,
  isValidEmail: (email: string) => boolean,
): string | null {
  if (!isPerformanceCreator) return null;
  if (!hubspotCompanyId.trim()) {
    return "HubSpot Company ID is required for Performance Creator brands";
  }
  if (!fields.vertical.trim()) {
    return "Grapevine vertical is required for Performance Creator brands";
  }
  if (!fields.contactEmail.trim() || !isValidEmail(fields.contactEmail)) {
    return "A valid Grapevine contact email is required for Performance Creator brands";
  }
  return null;
}

export function appendGrapevineFormData(
  formData: FormDataLike,
  isPerformanceCreator: boolean,
  hubspotCompanyId: string,
  fields: GrapevineBrandFields,
): void {
  // The brand service mirrors is_performance_creator onto the legacy
  // is_influencer column, so only the new name is sent.
  formData.append(
    "is_performance_creator",
    isPerformanceCreator ? "true" : "false",
  );
  formData.append("sync_to_grapevine", isPerformanceCreator ? "true" : "false");
  formData.append("hubspot_company_id", hubspotCompanyId);

  if (!isPerformanceCreator) return;

  formData.append("gv_slug", fields.slug);
  formData.append("gv_vertical", fields.vertical);
  formData.append("gv_contact_name", fields.contactName);
  formData.append("gv_contact_email", fields.contactEmail);
  formData.append("gv_homepage_url", fields.homepageUrl);
  formData.append("gv_tier", fields.tier);
}

export function getGrapevineEditPrefill(
  liftBrand: Record<string, unknown>,
  liveGrapevine?: Record<string, unknown> | null,
): GrapevineBrandFields & { hubspotCompanyId: string } {
  const live =
    (liveGrapevine?.grapevine_brand as Record<string, unknown> | undefined) ??
    liveGrapevine ??
    {};

  return {
    slug: firstPresent(live.gv_slug, live.slug),
    hubspotCompanyId: firstPresent(
      live.hubspot_company_id,
      live.hubspotCompanyId,
      liftBrand.hubspot_company_id,
    ),
    vertical: firstPresent(
      live.gv_vertical,
      live.verticals,
      live.vertical,
      liftBrand.vertical,
    ),
    contactName: firstPresent(live.gv_contact_name, live.contact_name),
    contactEmail: firstPresent(live.gv_contact_email, live.contact_email),
    homepageUrl: firstPresent(
      live.gv_homepage_url,
      live.homepage_url,
      live.homepage,
      liftBrand.domain,
    ),
    tier: firstPresent(live.gv_tier, live.tier),
  };
}

export type GrapevineEditPrefill = GrapevineBrandFields & {
  hubspotCompanyId: string;
};

/**
 * Fills any empty live Grapevine field from `fallback` (the values the admin
 * just submitted). Live data wins whenever it has a value.
 */
export function mergeGrapevinePrefill(
  live: GrapevineEditPrefill,
  fallback?: GrapevineEditPrefill | null,
): GrapevineEditPrefill {
  if (!fallback) return live;
  return {
    slug: live.slug.trim() ? live.slug : fallback.slug,
    hubspotCompanyId: live.hubspotCompanyId.trim()
      ? live.hubspotCompanyId
      : fallback.hubspotCompanyId,
    vertical: live.vertical.trim() ? live.vertical : fallback.vertical,
    contactName: live.contactName.trim() ? live.contactName : fallback.contactName,
    contactEmail: live.contactEmail.trim()
      ? live.contactEmail
      : fallback.contactEmail,
    homepageUrl: live.homepageUrl.trim() ? live.homepageUrl : fallback.homepageUrl,
    tier: live.tier.trim() ? live.tier : fallback.tier,
  };
}

/** Delays between retries when a just-created brand is not in Grapevine yet. */
export const GRAPEVINE_RECENT_RETRY_DELAYS_MS = [1000, 2000, 3000, 4000];

const isRetryableLookupError = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  // No response (network), not found yet, or a transient server error.
  return status === undefined || status === 404 || status >= 500;
};

/**
 * Brand creation notifies Grapevine fire-and-forget, so opening Edit right
 * after Add can hit Grapevine before it has the brand. Retry the lookup a few
 * times instead of failing on the first miss.
 */
export async function retryGrapevineLookup<T>(
  lookup: () => Promise<T>,
  delaysMs: number[] = GRAPEVINE_RECENT_RETRY_DELAYS_MS,
  isCancelled: () => boolean = () => false,
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= delaysMs.length; attempt += 1) {
    try {
      return await lookup();
    } catch (error) {
      lastError = error;
      if (
        attempt === delaysMs.length ||
        isCancelled() ||
        !isRetryableLookupError(error)
      ) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, delaysMs[attempt]));
      if (isCancelled()) break;
    }
  }
  throw lastError;
}
